import { useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FeeReceiptTermsDialog from "./FeeReceiptTermsDialog";
import FeeReceiptsView from "./FeeReceiptsView";
import FeeBooksView from "./FeeBooksView";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import MDTypography from "components/MDTypography";

const today = () => new Date().toISOString().split("T")[0];

const money = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const toId = (value) => String(value || "");

function FeeReceiptMultiple() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch (_error) {
      return {};
    }
  }, []);
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchRollNo, setSearchRollNo] = useState("");
  const [studentMatches, setStudentMatches] = useState([]);
  const [feeRows, setFeeRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentRows, setPaymentRows] = useState({});
  const [receiptDate, setReceiptDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [termDialogOpen, setTermDialogOpen] = useState(false);
  const [termLoading, setTermLoading] = useState(false);
  const [termYears, setTermYears] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [termConfiguration, setTermConfiguration] = useState(null);
  const [termPayment, setTermPayment] = useState({
    fee_book_id: "",
    year_number: 1,
    termno: 1,
    fee_amount: 0,
    paid_amount: 0,
    due_amount: 0,
    amount: "",
    payment_mode: "CASH",
    trn_ref_no: "",
    trn_ref_date: today(),
    remarks: "",
  });

  const [termSaving, setTermSaving] = useState(false);
  const authHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  const showMessage = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const comparisonYears = [1, 2, 3].map(
    (yearNumber) => termYears.find((year) => Number(year.year_number) === yearNumber) || null
  );

  const getYearTerm = (year, termno) =>
    year?.terms?.find((term) => Number(term.termno) === termno) || null;

  const getYearTotal = (year, field) =>
    (year?.terms || []).reduce((sum, term) => sum + Number(term[field] || 0), 0);

  const termComparisonRows = [
    ...[1, 2, 3].map((termno) => ({
      id: `term-${termno}`,
      description: `Term-${termno}`,
      year1: getYearTerm(comparisonYears[0], termno),
      year2: getYearTerm(comparisonYears[1], termno),
      year3: getYearTerm(comparisonYears[2], termno),
    })),
    {
      id: "concession",
      description: "Concession",
      year1: comparisonYears[0] ? getYearTotal(comparisonYears[0], "concession_amount") : null,
      year2: comparisonYears[1] ? getYearTotal(comparisonYears[1], "concession_amount") : null,
      year3: comparisonYears[2] ? getYearTotal(comparisonYears[2], "concession_amount") : null,
    },
    {
      id: "refund",
      description: "Refund",
      year1: comparisonYears[0] ? getYearTotal(comparisonYears[0], "refund_amount") : null,
      year2: comparisonYears[1] ? getYearTotal(comparisonYears[1], "refund_amount") : null,
      year3: comparisonYears[2] ? getYearTotal(comparisonYears[2], "refund_amount") : null,
    },
    {
      id: "action",
      description: "Action",
      year1: comparisonYears[0],
      year2: comparisonYears[1],
      year3: comparisonYears[2],
    },
  ];
  const resetPayments = () => {
    setFeeRows([]);
    setSelectedIds([]);
    setPaymentRows({});
  };

  const loadStudentFees = async (student) => {
    if (!student?.student_id) return;
    setLoading(true);
    resetPayments();
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/student/${student.student_id}/fee-books`,
        { headers: authHeaders() }
      );
      const rows = (data?.data || []).map((book, index) => {
        const due = Number(book.due_amount || 0);
        return {
          id: toId(book.fee_book_id),
          sno: index + 1,
          fee_book_id: toId(book.fee_book_id),
          feeHead: book.feeHead?.fee_head_name || "Fee",
          payType: book.feeHead?.pay_type || "",
          academicYear: book.feeHead?.academic_year || "",
          totalFee: Number(book.total_fee || book.fee_amount || 0),
          concession: Number(book.concession_amount || 0),
          refund: Number(book.refund_amount || 0),
          paid: Number(book.paid_amount || 0),
          due,
          fee_type_id: toId(book.feeHead?.fee_type_id),
          feeTypeName: String(
            book.feeHead?.feeType?.fee_type_name || book.feeHead?.feeType?.fee_type_code || ""
          )
            .trim()
            .toUpperCase(),
          year_number: Number(book.year_number || 0),
          termno: Number(book.termno || 0),
        };
      });
      setFeeRows(rows);
      setPaymentRows(
        Object.fromEntries(
          rows.map((row) => [
            row.id,
            {
              amount: row.due,
              payment_mode: "CASH",
              trn_ref_no: "",
              trn_ref_date: today(),
              remarks: "",
            },
          ])
        )
      );
      if (rows.length === 0) showMessage("No pending fee dues for this student", "info");
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to load fee dues", "error");
    } finally {
      setLoading(false);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setSearchRollNo(student?.student_rollno || "");
    setStudentMatches([]);
    await loadStudentFees(student);
  };

  const searchByRollNo = async () => {
    const searchText = searchRollNo.trim();
    if (!searchText) {
      showMessage("Enter roll number or student name", "warning");
      return;
    }
    setLoading(true);
    resetPayments();
    setSelectedStudent(null);
    setStudentMatches([]);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/students/search`,
        {
          headers: authHeaders(),
          params: { search: searchText },
        }
      );
      const matches = data?.data || [];
      if (matches.length === 0) {
        showMessage("Student Details are not found", "error");
        return;
      }
      if (matches.length === 1) {
        await selectStudent(matches[0]);
        return;
      }
      setStudentMatches(matches);
      showMessage("Multiple students found. Select one student from the list.", "info");
    } catch (error) {
      showMessage(error.response?.data?.message || "Student Details are not found", "error");
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentRow = (id, field, value) => {
    setPaymentRows((current) => ({
      ...current,
      [id]: {
        ...current[id],
        [field]: value,
        ...(field === "payment_mode" && value === "CASH"
          ? { trn_ref_no: "", trn_ref_date: today() }
          : {}),
      },
    }));
  };

  const selectedRows = feeRows.filter((row) => selectedIds.includes(row.id));
  const selectedTotal = selectedRows.reduce(
    (sum, row) => sum + Number(paymentRows[row.id]?.amount || 0),
    0
  );

  const validatePayments = () => {
    if (!selectedStudent) return "Select a student first";
    if (selectedRows.length === 0) return "Select at least one fee row";
    for (const row of selectedRows) {
      const payment = paymentRows[row.id] || {};
      const amount = Number(payment.amount || 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return `Enter a valid amount for ${row.feeHead}`;
      }
      if (amount > row.due) {
        return `Amount cannot exceed due for ${row.feeHead}`;
      }
      if (!payment.payment_mode) return `Select payment mode for ${row.feeHead}`;
      if (payment.payment_mode !== "CASH" && !String(payment.trn_ref_no || "").trim()) {
        return `Enter transaction reference for ${row.feeHead}`;
      }
    }
    return "";
  };

  const openPdfPreview = async (receiptIds) => {
    const { data } = await axios.get(
      `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/preview-pdf`,
      {
        headers: authHeaders(),
        params: { receiptIds: receiptIds.join(",") },
        responseType: "blob",
      }
    );
    const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const buildPaymentPayload = () => ({
    student_id: selectedStudent.student_id,
    receipt_date: receiptDate,
    created_by: user?.user_email || "",
    payments: selectedRows.map((row) => {
      const payment = paymentRows[row.id];
      return {
        fee_book_id: row.fee_book_id,
        amount: Number(payment.amount),
        payment_mode: payment.payment_mode,
        trn_ref_no: payment.payment_mode === "CASH" ? "" : payment.trn_ref_no,
        trn_ref_date: payment.payment_mode === "CASH" ? null : payment.trn_ref_date,
        remarks: payment.remarks || "",
      };
    }),
  });

  const openTermFeeDialog = async (feeRow) => {
    if (!selectedStudent) {
      showMessage("Select a student first", "warning");
      return;
    }

    setTermDialogOpen(true);
    setTermLoading(true);
    setTermYears([]);
    setSelectedTerm(null);

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/student/${selectedStudent.student_id}/tuition-terms`,
        {
          headers: authHeaders(),
          params: {
            fee_book_id: feeRow.fee_book_id,
          },
        }
      );

      setTermYears(data?.data || []);
    } catch (error) {
      setTermDialogOpen(false);
      showMessage(error.response?.data?.message || "Unable to load tuition term fees", "error");
    } finally {
      setTermLoading(false);
    }
  };

  const openTermPayment = (year, term) => {
    if (Number(term.due_amount) <= 0) {
      showMessage("This term has no outstanding amount", "info");
      return;
    }

    setSelectedTerm({
      year_number: Number(year.year_number),
      academic_year: year.academic_year,
      termno: Number(term.termno),
      fee_book_id: String(year.fee_book_id),
      fee_amount: Number(term.fee_amount || 0),
      paid_amount: Number(term.paid_amount || 0),
      due_amount: Number(term.due_amount || 0),
    });

    setTermPayment({
      fee_book_id: String(year.fee_book_id),
      year_number: Number(year.year_number),
      termno: Number(term.termno),
      fee_amount: Number(term.fee_amount || 0),
      paid_amount: Number(term.paid_amount || 0),
      due_amount: Number(term.due_amount || 0),
      amount: String(term.due_amount),
      payment_mode: "CASH",
      trn_ref_no: "",
      trn_ref_date: today(),
      remarks: "",
    });
  };
  const submitTermPayment = async () => {
    if (!selectedStudent || !selectedTerm) {
      showMessage("Select a student and term first", "warning");
      return;
    }

    const amount = Number(termPayment.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      showMessage("Enter a valid payment amount", "warning");
      return;
    }

    if (amount > selectedTerm.due_amount + 0.001) {
      showMessage("Payment cannot exceed the selected term's due", "warning");
      return;
    }

    if (termPayment.payment_mode !== "CASH" && !String(termPayment.trn_ref_no || "").trim()) {
      showMessage("Enter the transaction reference number", "warning");
      return;
    }

    if (termPayment.payment_mode !== "CASH" && !termPayment.trn_ref_date) {
      showMessage("Enter the transaction date", "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm Term Fee Payment?",
      text: `Year ${selectedTerm.year_number}, Term ${selectedTerm.termno}: Rs. ${money(amount)}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Pay and Print",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1976d2",
    });

    if (!confirm.isConfirmed) return;

    setTermSaving(true);

    try {
      const payload = {
        student_id: selectedStudent.student_id,
        receipt_date: receiptDate,
        created_by: user?.user_email || "",
        payments: [
          {
            fee_book_id: selectedTerm.fee_book_id,
            termno: selectedTerm.termno,
            amount,
            payment_mode: termPayment.payment_mode,
            trn_ref_no: termPayment.payment_mode === "CASH" ? "" : termPayment.trn_ref_no.trim(),
            trn_ref_date: termPayment.payment_mode === "CASH" ? null : termPayment.trn_ref_date,
            remarks: termPayment.remarks || "",
          },
        ],
      };

      // Reuse the existing percentage-validation endpoint.
      await axios.post(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/validate-fee-percentage`,
        payload,
        { headers: authHeaders() }
      );

      // Reuse the existing receipt-creation service.
      const { data } = await axios.post(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}`,
        payload,
        { headers: authHeaders() }
      );

      const receiptIds = (data?.data || []).map((item) => item.fee_receipt_id).filter(Boolean);

      if (!receiptIds.length) {
        throw new Error("Payment saved, but no receipt ID was returned");
      }

      showMessage("Term fee payment saved successfully", "success");

      // Existing PDF contains ORIGINAL and DUPLICATE copies.
      await openPdfPreview(receiptIds);

      // Refresh the normal fee grid and term-wise balances.
      await loadStudentFees(selectedStudent);

      const { data: refreshed } = await axios.get(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/student/${selectedStudent.student_id}/tuition-terms`,
        {
          headers: authHeaders(),
          params: {
            fee_book_id: selectedTerm.fee_book_id,
          },
        }
      );

      setTermYears(refreshed?.data || []);
      setSelectedTerm(null);
      setTermDialogOpen(false);
    } catch (error) {
      showMessage(
        error.response?.data?.message || error.message || "Unable to save term fee payment",
        "error"
      );
    } finally {
      setTermSaving(false);
    }
  };
  const submitPayments = async () => {
    const validationMessage = validatePayments();
    if (validationMessage) {
      showMessage(validationMessage, "warning");
      return;
    }

    let payload = buildPaymentPayload();
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/validate-fee-percentage`,
        payload,
        { headers: authHeaders() }
      );
    } catch (error) {
      const response = error.response?.data;
      const detail = response?.details?.[0];
      if (response?.code === "FEE_PERCENT_AMOUNT_REQUIRED" && detail) {
        const requiredAmount = Number(detail.required_payment || 0);
        const amountPrompt = await Swal.fire({
          title: `${detail.fee_name} Percentage Payment`,
          html: `${Number(
            detail.percent
          )}% must be completed.<br/>Required percentage amount: <b>Rs. ${money(
            detail.required
          )}</b><br/>Already completed: <b>Rs. ${money(
            detail.completed
          )}</b><br/>Please enter exactly: <b>Rs. ${money(requiredAmount)}</b>`,
          input: "number",
          inputPlaceholder: `Enter Rs. ${money(requiredAmount)}`,
          inputAttributes: { min: "0.01", step: "0.01" },
          showCancelButton: true,
          confirmButtonText: "Use this amount",
          preConfirm: (value) => {
            const entered = Number(value);
            if (!Number.isFinite(entered) || Math.abs(entered - requiredAmount) > 0.001) {
              Swal.showValidationMessage(`Enter exactly Rs. ${money(requiredAmount)}`);
              return false;
            }
            return entered;
          },
        });

        if (!amountPrompt.isConfirmed) {
          setSaving(false);
          return;
        }
        const enteredAmount = Number(amountPrompt.value);
        payload = {
          ...payload,
          payments: payload.payments.map((payment) =>
            String(payment.fee_book_id) === String(detail.fee_book_id)
              ? { ...payment, amount: enteredAmount }
              : payment
          ),
        };
        updatePaymentRow(String(detail.fee_book_id), "amount", enteredAmount);

        try {
          await axios.post(
            `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/validate-fee-percentage`,
            payload,
            { headers: authHeaders() }
          );
        } catch (retryError) {
          showMessage(
            retryError.response?.data?.message || "Fee percentage requirement is not completed",
            "error"
          );
          setSaving(false);
          return;
        }
      } else {
        showMessage(response?.message || "Fee percentage requirement is not completed", "error");
        setSaving(false);
        return;
      }
    }

    const confirmedTotal = payload.payments.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0
    );

    const confirm = await Swal.fire({
      title: "Confirm payment?",
      text: `Do you want to confirm payment of Rs. ${money(confirmedTotal)} for ${
        selectedRows.length
      } fee item(s)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Confirm payment?",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) {
      setSaving(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}`,
        payload,
        { headers: authHeaders() }
      );

      const receiptIds = (data?.data || []).map((item) => item.fee_receipt_id).filter(Boolean);

      showMessage("Payment saved successfully", "success");
      await openPdfPreview(receiptIds);
      await loadStudentFees(selectedStudent);
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Unable to save payment, Verify details",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    {
      field: "feeHead",
      headerName: "Fee Head",
      minWidth: 360,
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.35, py: 1 }}>{params.value}</Box>
      ),
    },
    /*   { field: "payType", headerName: "Pay Type", minWidth: 105, flex: 0.6 },
    { field: "academicYear", headerName: "Academic Year", minWidth: 120, flex: 0.7 }, */
    {
      field: "totalFee",
      headerName: "Total",
      minWidth: 95,
      renderCell: (params) => money(params.value),
    },
    {
      field: "concession",
      headerName: "Concession",
      minWidth: 115,
      renderCell: (params) => money(params.value),
    },
    {
      field: "paid",
      headerName: "Paid",
      minWidth: 95,
      renderCell: (params) => money(params.value),
    },
    {
      field: "due",
      headerName: "Due",
      minWidth: 145,
      flex: 0.8,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            color: "#7f1d1d",
            bgcolor: "#fee2e2",
            border: "1px solid #991b1b",
            borderRadius: 1,
            fontWeight: 800,
            px: 1,
            py: 0.5,
            lineHeight: 1.2,
            textAlign: "right",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {money(params.value)}
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Pay Amount",
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <TextField
          type="number"
          size="small"
          value={paymentRows[params.row.id]?.amount || ""}
          inputProps={{ min: 0, max: params.row.due, step: "1.00" }}
          onChange={(event) => updatePaymentRow(params.row.id, "amount", event.target.value)}
          sx={{
            width: "100%",
            bgcolor: "#fee2e2",
            "& .MuiOutlinedInput-root": { height: "32px" },
            "& .MuiInputBase-input": {
              fontSize: "18px",
              fontWeight: 800,
              color: "#0c0cd2",
              textAlign: "center",
              /* Chrome, Edge, Safari */ "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                { WebkitAppearance: "none", margin: 0 },
              /* Firefox */ MozAppearance: "textfield",
            },
          }}
        />
      ),
    },

    {
      field: "mode",
      headerName: "Mode",
      minWidth: 125,
      sortable: false,
      renderCell: (params) => (
        <TextField
          select
          size="medium"
          fullWidth
          SelectProps={{ native: true }}
          value={paymentRows[params.row.id]?.payment_mode || "CASH"}
          onChange={(event) => updatePaymentRow(params.row.id, "payment_mode", event.target.value)}
          InputLabelProps={{ shrink: true }}
        >
          <option value="CASH">Cash</option>
          <option value="ONLINE">Online</option>
          <option value="DD">DD</option>
          <option value="CHEQUE">Cheque</option>
        </TextField>
      ),
    },
    {
      field: "ref",
      headerName: "txn_ref_no",
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const payment = paymentRows[params.row.id] || {};
        return (
          <TextField
            size="medium"
            disabled={payment.payment_mode === "CASH"}
            value={payment.trn_ref_no || ""}
            onChange={(event) => updatePaymentRow(params.row.id, "trn_ref_no", event.target.value)}
          />
        );
      },
    },
    {
      field: "refDate",
      headerName: "Ref Date",
      minWidth: 145,
      sortable: false,
      renderCell: (params) => {
        const payment = paymentRows[params.row.id] || {};
        return (
          <TextField
            type="date"
            size="medium"
            disabled={payment.payment_mode === "CASH"}
            value={payment.trn_ref_date || today()}
            inputProps={{ max: today() }}
            onChange={(event) =>
              updatePaymentRow(params.row.id, "trn_ref_date", event.target.value)
            }
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      {/*  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <ReceiptLongIcon color="primary" />
        <MDTypography variant="h5">Collect Multiple Fee </MDTypography>
      </Stack> */}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              label="Receipt Date"
              type="date"
              align="right"
              fullWidth
              size="medium"
              value={receiptDate}
              inputProps={{ max: today() }}
              onChange={(event) => setReceiptDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Roll Number / Student Name"
              fullWidth
              size="medium"
              value={searchRollNo}
              onChange={(event) => setSearchRollNo(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") searchByRollNo();
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "1.1rem",
                  fontWeight: 500,
                  py: 1.5,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "1.05rem",
                },
                "& .MuiInputLabel-shrink": {
                  fontSize: "1.05rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={searchByRollNo}
              sx={{ color: "#fff" }}
            >
              Search
            </Button>
          </Grid>

          <Grid item xs={12} md={2}>
            <FeeReceiptTermsDialog
              selectedStudent={selectedStudent}
              onPaymentSuccess={async () => {
                if (selectedStudent) {
                  await loadStudentFees(selectedStudent);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FeeBooksView selectedStudent={selectedStudent} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FeeReceiptsView selectedStudent={selectedStudent} />
          </Grid>
        </Grid>
      </Paper>

      {studentMatches.length > 0 && (
        <Paper variant="outlined" sx={{ height: 260, width: "100%", mb: 2 }}>
          <DataGrid
            rows={studentMatches.map((student) => ({
              ...student,
              id: toId(student.student_id),
              branchName: student.branch?.branch_name || "",
              batchName: student.admittedbatch?.admittedbatch || "",
            }))}
            columns={[
              { field: "student_rollno", headerName: "Roll No", minWidth: 140, flex: 0.8 },
              { field: "student_name", headerName: "Student Name", minWidth: 220, flex: 1.5 },
              { field: "branchName", headerName: "Branch", minWidth: 160, flex: 1 },
              { field: "batchName", headerName: "Batch", minWidth: 120, flex: 0.8 },
              {
                field: "select",
                headerName: "Select",
                width: 110,
                sortable: false,
                renderCell: (params) => (
                  <Button
                    size="medium"
                    variant="contained"
                    sx={{ color: "#fff" }}
                    onClick={() => selectStudent(params.row)}
                  >
                    Select
                  </Button>
                ),
              },
            ]}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
          />
        </Paper>
      )}

      {selectedStudent && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#fcf8fa" }}>
          <Grid container spacing={1}>
            <Grid item xs={6} md={2}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Roll No
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#2e7d32", fontWeight: "bold", fontSize: "16px" }}
              >
                {selectedStudent.student_rollno}
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={3}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Student Name
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#1976d2", fontWeight: "bold", fontSize: "16px" }}
              >
                {selectedStudent.student_name}
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={2}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Branch Name
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#1976d2", fontWeight: "bold", fontSize: "16px" }}
              >
                {selectedStudent.branch.branch_name}
              </MDTypography>
            </Grid>
            <Grid item xs={6} md={1}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                FeeHeads
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#ed6c02", fontWeight: "bold", fontSize: "16px" }}
              >
                {selectedRows.length}
              </MDTypography>
            </Grid>

            <Grid item xs={12} md={2}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Total Payment
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#d32f2f", fontWeight: "bold", fontSize: "18px" }}
              >
                Rs. {money(selectedTotal)}
              </MDTypography>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PrintIcon />}
                disabled={saving || selectedRows.length === 0}
                onClick={submitPayments}
                sx={{ color: "#fff", fontWeight: "bold", fontSize: "14px" }}
              >
                {saving ? "Saving" : "Pay Now"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ height: 530, width: "100%" }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
            <CircularProgress />
          </Stack>
        ) : (
          <DataGrid
            rows={feeRows}
            columns={columns}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectedIds}
            onSelectionModelChange={(newSelection) => {
              setSelectedIds(newSelection.map(String));
            }}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            getRowHeight={() => "auto"}
            sx={{
              "& .MuiDataGrid-cell": {
                alignItems: "flex-start",
                py: 1,
              },
              "& .MuiCheckbox-root": {
                color: "#000",
              },
              "& .MuiCheckbox-root .MuiSvgIcon-root": {
                border: "1.5px solid #000",
                borderRadius: "2px",
              },
              "& .MuiCheckbox-root.Mui-checked": {
                color: "#000",
              },
              "& .MuiCheckbox-root.MuiCheckbox-indeterminate": {
                color: "#000",
              },
            }}
          />
        )}
      </Paper>
      <Dialog
        open={termDialogOpen}
        onClose={() => {
          if (!termSaving) {
            setTermDialogOpen(false);
            setSelectedTerm(null);
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          Tuition Term Fees — {selectedStudent?.student_name}
          {" ("}
          {selectedStudent?.student_rollno}
          {")"}
        </DialogTitle>

        <DialogContent dividers>
          {termLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%" }}>
                <DataGrid
                  autoHeight
                  rows={termComparisonRows}
                  columns={[
                    {
                      field: "description",
                      headerName: "Fee Description",
                      minWidth: 150,
                      flex: 1.2,
                      sortable: false,
                      renderCell: (params) => (
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          color={params.row.id === "action" ? "info" : "dark"}
                        >
                          {params.value}
                        </MDTypography>
                      ),
                    },
                    ...[
                      { field: "year1", headerName: "I Year" },
                      { field: "year2", headerName: "II Year" },
                      { field: "year3", headerName: "III Year" },
                    ].map((column, index) => ({
                      ...column,
                      minWidth: 170,
                      flex: 1,
                      sortable: false,
                      align: "center",
                      headerAlign: "center",
                      renderCell: (params) => {
                        const value = params.row[column.field];

                        if (value == null) {
                          return "—";
                        }

                        // Term fee: Paid amount of total term fee.
                        if (params.row.id.startsWith("term-")) {
                          const paid = Number(value.paid_amount || 0);
                          const fee = Number(value.fee_amount || 0);

                          return (
                            <Box textAlign="center">
                              <MDTypography variant="button" fontWeight="medium">
                                {money(paid)} of {money(fee)}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                display="block"
                                sx={{
                                  color: Number(value.due_amount || 0) > 0 ? "#c62828" : "#2e7d32",
                                }}
                              >
                                Due: {money(value.due_amount || 0)}
                              </MDTypography>
                            </Box>
                          );
                        }

                        // Concession and refund rows.
                        if (params.row.id === "concession" || params.row.id === "refund") {
                          return money(Number(value || 0));
                        }

                        // One payment action per study year.
                        if (params.row.id === "action") {
                          const year = value;

                          const dueTerm = (year.terms || []).find(
                            (term) => Number(term.due_amount || 0) > 0
                          );

                          return (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!dueTerm || termSaving}
                              onClick={() => {
                                if (dueTerm) {
                                  openTermPayment(year, dueTerm);
                                }
                              }}
                              sx={{
                                color: "#fff",
                                whiteSpace: "nowrap",
                                minWidth: 95,
                                backgroundColor: dueTerm ? "#1976d2" : "#9e9e9e",
                                "&:hover": {
                                  backgroundColor: dueTerm ? "#1565c0" : "#9e9e9e",
                                },
                                "&.Mui-disabled": {
                                  color: "#fff",
                                  backgroundColor: "#9e9e9e",
                                },
                              }}
                            >
                              {dueTerm ? "Pay Now" : "No Due"}
                            </Button>
                          );
                        }

                        return "—";
                      },
                    })),
                  ]}
                  disableSelectionOnClick
                  hideFooter
                  sx={{
                    width: "100%",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#e3f2fd",
                      fontWeight: 700,
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 700,
                    },
                    "& .MuiDataGrid-cell": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "normal",
                    },
                    "& .MuiDataGrid-cell:first-of-type": {
                      justifyContent: "flex-start",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#fafafa",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#f1f8ff",
                    },
                    "& .MuiDataGrid-row:last-child": {
                      backgroundColor: "#e3f2fd",
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>

              {selectedTerm && (
                <Box mt={3} p={2} border="1px solid #90caf9" borderRadius={2}>
                  <MDTypography variant="h6" mb={2}>
                    Pay Year {selectedTerm.year_number} — Term {selectedTerm.termno}
                  </MDTypography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Actual Term Fee"
                        fullWidth
                        value={money(selectedTerm.fee_amount)}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Already Paid"
                        fullWidth
                        value={money(selectedTerm.paid_amount)}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Outstanding Term Due"
                        fullWidth
                        value={money(selectedTerm.due_amount)}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Payment Amount"
                        type="number"
                        fullWidth
                        required
                        value={termPayment.amount}
                        onChange={(event) =>
                          setTermPayment((current) => ({
                            ...current,
                            amount: event.target.value,
                          }))
                        }
                        inputProps={{
                          min: 0.01,
                          max: selectedTerm.due_amount,
                          step: "0.01",
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        SelectProps={{ native: true }}
                        label="Payment Mode"
                        fullWidth
                        value={termPayment.payment_mode}
                        onChange={(event) => {
                          const payment_mode = event.target.value;

                          setTermPayment((current) => ({
                            ...current,
                            payment_mode,
                            trn_ref_no: payment_mode === "CASH" ? "" : current.trn_ref_no,
                            trn_ref_date:
                              payment_mode === "CASH" ? today() : current.trn_ref_date || today(),
                          }));
                        }}
                        InputLabelProps={{ shrink: true }}
                      >
                        <option value="CASH">Cash</option>
                        <option value="ONLINE">Online</option>
                        <option value="DD">DD</option>
                        <option value="CHEQUE">Cheque</option>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Transaction Number"
                        fullWidth
                        disabled={termPayment.payment_mode === "CASH"}
                        required={termPayment.payment_mode !== "CASH"}
                        value={termPayment.trn_ref_no}
                        onChange={(event) =>
                          setTermPayment((current) => ({
                            ...current,
                            trn_ref_no: event.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Transaction Date"
                        type="date"
                        fullWidth
                        disabled={termPayment.payment_mode === "CASH"}
                        value={termPayment.trn_ref_date}
                        inputProps={{ max: today() }}
                        onChange={(event) =>
                          setTermPayment((current) => ({
                            ...current,
                            trn_ref_date: event.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Remarks"
                        fullWidth
                        multiline
                        minRows={2}
                        value={termPayment.remarks}
                        onChange={(event) =>
                          setTermPayment((current) => ({
                            ...current,
                            remarks: event.target.value,
                          }))
                        }
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="flex-end" mt={2} spacing={1}>
                    <Button
                      variant="outlined"
                      disabled={termSaving}
                      onClick={() => setSelectedTerm(null)}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<PrintIcon />}
                      disabled={termSaving}
                      onClick={submitTermPayment}
                      sx={{ color: "#fff" }}
                    >
                      {termSaving ? "Processing..." : "Pay and Print Receipt"}
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            disabled={termSaving}
            onClick={() => {
              setTermDialogOpen(false);
              setSelectedTerm(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FeeReceiptMultiple;
